import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

// Utils
import { LISTING_STATE_CLOSED } from '../../util/types';
import { socialDescription } from '../../util/socialDescription';
import { presentMeasurements, showMeasurements } from '../../util/listingMeasurements';

// Global ducks
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import { listingPath } from '../../util/urlHelpers';
import { isDeletedListing } from '../../util/listingDeletion';
import { findVisibleCategory } from '../../util/listingFieldLabels';
import { deleteListing } from '../../util/api';
import { pathByRouteName } from '../../util/routes';
import {
  Page,
  LayoutSingleColumn,
  Modal,
  WithdrawalNotice,
  DeleteListingModal,
} from '../../components';

// Related modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import { getRatingStats } from '../../util/reviews';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
  getDerivedRenderData,
} from './ListingPage.shared';

import Notifications from './Notifications/Notifications';
import SectionGallery from './SectionGallery';
import SimilarListings from './SimilarListings/SimilarListings';
import ListingPageAccessWrapper from './ListingPageAccessWrapper';

// New ZUPRIZE sub-components
import ProductHeader from './ProductHeader/ProductHeader';
import BuyNowButton from './BuyNowButton/BuyNowButton';
import TrustBadges from './TrustBadges/TrustBadges';
import DescriptionSection from './DescriptionSection/DescriptionSection';
import DetailSection from './DetailSection/DetailSection';
import SellerCard from './SellerCard/SellerCard';
import InquiryForm from './InquiryForm/InquiryForm';

import FooterContainer from '../FooterContainer/FooterContainer';
import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;
const CONTACT_USER_LINK = 'inquiryModalContactUserLink';

/**
 * Resolve the human-readable label for a `multi-enum` listing field value
 * (an array of option keys) to a comma-joined string of labels.
 */
const formatMultiEnumLabels = (listingFields, key, values) => {
  if (!Array.isArray(values) || values.length === 0) return null;
  const field = listingFields?.find(f => f.key === key);
  const optionMap = new Map((field?.enumOptions || []).map(o => [o.option, o.label]));
  const labels = values.map(v => optionMap.get(v) || v).filter(Boolean);
  return labels.length > 0 ? labels.join(', ') : null;
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    sellerReviews,
  } = props;

  // Seller's average rating for the SellerCard — computed on the fly
  // from the public reviews about the seller (ListingPage.duck
  // fetchSellerReviews + util/reviews).
  const sellerRatingStats = getRatingStats(sellerReviews);

  const derivedData = getDerivedRenderData({
    rawParams,
    getListing,
    getOwnListing,
    showOwnListingsOnly,
    currentUser,
    config,
    intl,
    location,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
    longWordClassName: css.longWord,
    payoutDetailsWarningClassName: css.payoutDetailsWarning,
  });
  const {
    listingConfig,
    listingId,
    isVariant,
    currentListing,
    listingSlug,
    params,
    listingPathParamType,
    listingTab,
    description,
    price,
    title,
    publicData,
    isOwnListing,
    showListingImage,
    showDescription,
    ensuredAuthor,
    authorDisplayName,
    schemaTitle,
    seoName,
    facebookImages,
    twitterImages,
    schemaImages,
    productURL,
    availabilityMaybe,
    noIndexMaybe,
    hasInvalidListingData,
  } = derivedData;

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  if (hasInvalidListingData) {
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }

  // Deleted by its seller ("Slett annonse"): gone for everyone else, with a
  // 410 so search engines drop it. The seller still sees it, marked deleted.
  if (isDeletedListing(currentListing) && !isOwnListing) {
    // The way on: more of its category, if buyers can still browse it.
    const goneCategory = findVisibleCategory(
      config.categoryConfiguration?.categories,
      currentListing.attributes.publicData
    );
    return (
      <NotFoundPage staticContext={props.staticContext} isGone goneCategory={goneCategory} />
    );
  }

  // After a deletion the listing has left the seller's list too: back to it.
  const handleDeleteListing = (reason, reasonText) =>
    deleteListing(currentListing.id.uuid, reason, reasonText).then(() => {
      setDeleteModalOpen(false);
      history.push(pathByRouteName('ManageListingsPage', routeConfiguration));
    });

  const isClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;

  // Out of stock: the simplified Buy Now flow skips the stock counter, but a
  // sold-out item (currentStock === 0) must still not be orderable — a
  // published listing stays reachable by direct URL even once it's hidden
  // from search, so without this guard a buyer could oversell it.
  const currentStock = currentListing.currentStock?.attributes?.quantity;
  const isOutOfStock = currentStock === 0;

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    setInitialValues,
    location,
    setInquiryModalOpen,
  });
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });

  // ZUPRIZE simplified Buy Now flow: skip booking dates, skip stock
  // counter, skip negotiation. The seller-side "default-purchase" process
  // accepts a minimal `{ deliveryMethod, quantity }` payload — CheckoutPage
  // collects the shipping address and runs the Stripe flow from there.
  const onBuyNow = () => {
    if (isOwnListing || isClosed || isOutOfStock) {
      window.scrollTo(0, 0);
      return;
    }
    const onSubmit = handleSubmit({
      ...commonParams,
      currentUser,
      callSetInitialValues,
      getListing,
      onInitializeCardPaymentData,
    });
    onSubmit({ deliveryMethod: 'shipping', quantity: 1 });
  };

  // Mal (measurements) renders in the same "label + value" shape as Materiale
  // so the two detail blocks read consistently, and is scoped to the same
  // categories the seller is offered the fields on. Only the measurements
  // actually filled in are listed.
  const maalText = showMeasurements(publicData)
    ? presentMeasurements(publicData?.maal)
        .map(({ key, value }) => {
          const label = intl.formatMessage({ id: `ListingPage.maal.${key}` });
          const amount = intl.formatMessage({ id: 'ListingPage.maalValue' }, { value });
          return `${label} ${amount}`;
        })
        .join(', ')
    : '';

  // Surface materiale (multi-enum) as a comma-separated list of labels.
  const materialeText = formatMultiEnumLabels(
    listingConfig.listingFields,
    'materiale',
    publicData?.materiale
  );

  // What a shared link previews as. The page description is the full listing
  // text; the preview wants the facts a buyer scans: brand, size, price.
  const socialSharingDescription = socialDescription({
    listingFields: listingConfig.listingFields,
    publicData,
    description,
    price,
    marketplaceCurrency: config.currency,
    intl,
  });
  // Facebook renders this as a price strip under a product card.
  const productPrice = priceForSchemaMaybe(price);
  const productPriceMaybe = productPrice.price
    ? { productPrice: { amount: productPrice.price, currency: productPrice.priceCurrency } }
    : {};

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      openGraphType="product"
      // Page reads the share preview text off `socialSharing`, not a flat prop.
      // The bare listing title, without the "| Zuprize" the browser tab and
      // search results carry: on a shared card the site name is already the
      // domain above the image and og:site_name beside it.
      socialSharing={{ title, description: socialSharingDescription }}
      {...productPriceMaybe}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      {...noIndexMaybe}
      // The listing's main address, from its current title: the same as the
      // links on the site, whatever slug (or none) this visit came in on.
      canonicalPath={listingPath(listingId.uuid, currentListing.attributes.title)}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: seoName,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={topbar}
        footer={
          <div className={css.footerDesktopOnly}>
            <FooterContainer />
          </div>
        }
      >
        <div className={css.contentWrapper}>
          {/* Owner-facing action bars (payout warnings, listing-state CTAs,
              edit link). The wrapper auto-collapses (`:empty` rule) when
              every ActionBar inside Notifications returns null. */}
          <div className={css.notificationsWrapper}>
            <Notifications
              mounted={mounted}
              listing={currentListing}
              isOwnListing={isOwnListing}
              noPayoutDetailsSetWithOwnListing={derivedData.noPayoutDetailsSetWithOwnListing}
              currentUser={currentUser}
              className={css.actionBar}
              onDeleteListing={isOwnListing ? () => setDeleteModalOpen(true) : null}
              editParams={{
                id: listingId.uuid,
                slug: listingSlug,
                type: listingPathParamType,
                tab: listingTab,
              }}
            />
          </div>

          <div className={css.gridLayout}>
            <div className={css.galleryColumn}>
              {showListingImage ? (
                <SectionGallery
                  listing={currentListing}
                  author={ensuredAuthor}
                  variantPrefix={config.layout.listingImage.variantPrefix}
                  listingFields={listingConfig.listingFields}
                  marketplaceRootURL={config.marketplaceRootURL}
                  facebookAppId={process.env.REACT_APP_FACEBOOK_APP_ID}
                  onManageDisableScrolling={onManageDisableScrolling}
                />
              ) : null}
            </div>

            <div className={css.detailColumn}>
              <ProductHeader listing={currentListing} listingFields={listingConfig.listingFields} />

              <BuyNowButton
                onBuy={onBuyNow}
                isOwnListing={isOwnListing}
                isClosed={isClosed}
                isOutOfStock={isOutOfStock}
              />

              {/* The item first, right after "Kjøp nå" (Guro). Always render the
                  description when one exists.
                  `showDescription` is only true when the listing-type
                  config opts into it via `defaultListingFields.description`;
                  if the operator forgot that flag in Console, we still
                  want to show whatever the seller wrote. */}
              {description || materialeText || maalText ? (
                <div className={css.detailSections}>
                  {description ? <DescriptionSection text={description} /> : null}

                  {materialeText ? (
                    <DetailSection
                      heading={intl.formatMessage({ id: 'ListingPage.materialeHeading' })}
                      value={materialeText}
                    />
                  ) : null}

                  {maalText ? (
                    <DetailSection
                      heading={intl.formatMessage({ id: 'ListingPage.maalHeading' })}
                      value={maalText}
                    />
                  ) : null}
                </div>
              ) : null}

              <WithdrawalNotice seller={ensuredAuthor} />

              <TrustBadges />

              <SellerCard
                author={ensuredAuthor}
                authorDisplayName={authorDisplayName}
                rating={sellerRatingStats.average}
                reviewCount={sellerRatingStats.count}
                onContactUser={onContactUser}
              />
            </div>
          </div>

          <SimilarListings listing={currentListing} />
        </div>

        <Modal
          id="ListingPage.inquiry"
          contentClassName={css.inquiryModalContent}
          isOpen={isAuthenticated && inquiryModalOpen}
          onClose={() => setInquiryModalOpen(false)}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
          focusElementId={CONTACT_USER_LINK}
        >
          <InquiryForm
            className={css.inquiryForm}
            submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
            listingTitle={title}
            authorDisplayName={authorDisplayName}
            sendInquiryError={sendInquiryError}
            onSubmit={onSubmitInquiry}
            inProgress={sendInquiryInProgress}
          />
        </Modal>
        {isOwnListing && deleteModalOpen ? (
          <DeleteListingModal
            id="ListingPage.deleteListing"
            isOpen
            onCloseModal={() => setDeleteModalOpen(false)}
            onManageDisableScrolling={onManageDisableScrolling}
            onDeleteListing={handleDeleteListing}
            focusElementId="ListingPage.deleteListingButton"
          />
        ) : null}
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component (carousel layout).
 *
 * @returns {JSX.Element}
 */
const ListingPage = props => {
  const dispatch = useDispatch();
  const store = useStore();

  const { isAuthenticated } = useSelector(state => state.auth);
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    sellerReviews,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = useSelector(state => state.ListingPage);
  const currentUser = useSelector(state => state.user?.currentUser);
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const getListing = useCallback(
    id => {
      const state = store.getState();
      const ref = { id, type: 'listing' };
      const listings = getMarketplaceEntities(state, [ref]);
      return listings.length === 1 ? listings[0] : null;
    },
    [store]
  );
  const getOwnListing = useCallback(
    id => {
      const state = store.getState();
      const ref = { id, type: 'ownListing' };
      const listings = getMarketplaceEntities(state, [ref]);
      return listings.length === 1 ? listings[0] : null;
    },
    [store]
  );

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );
  const callSetInitialValues = useCallback(
    (setInitialValuesFn, values, saveToSessionStorage) =>
      dispatch(setInitialValuesFn(values, saveToSessionStorage)),
    [dispatch]
  );
  const onFetchTransactionLineItems = useCallback(p => dispatch(fetchTransactionLineItems(p)), [
    dispatch,
  ]);
  const onSendInquiry = useCallback((listing, message) => dispatch(sendInquiry(listing, message)), [
    dispatch,
  ]);
  const onInitializeCardPaymentData = useCallback(() => dispatch(initializeCardPaymentData()), [
    dispatch,
  ]);
  const onFetchTimeSlots = useCallback(
    (listingId, start, end, timeZone, options) =>
      dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)),
    [dispatch]
  );

  return (
    <ListingPageAccessWrapper
      {...props}
      PageComponent={ListingPageComponent}
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      getListing={getListing}
      getOwnListing={getOwnListing}
      scrollingDisabled={scrollingDisabled}
      inquiryModalOpenForListingId={inquiryModalOpenForListingId}
      showListingError={showListingError}
      reviews={reviews}
      fetchReviewsError={fetchReviewsError}
      sellerReviews={sellerReviews}
      monthlyTimeSlots={monthlyTimeSlots}
      timeSlotsForDate={timeSlotsForDate}
      lineItems={lineItems}
      fetchLineItemsInProgress={fetchLineItemsInProgress}
      fetchLineItemsError={fetchLineItemsError}
      sendInquiryInProgress={sendInquiryInProgress}
      sendInquiryError={sendInquiryError}
      onManageDisableScrolling={onManageDisableScrolling}
      callSetInitialValues={callSetInitialValues}
      onFetchTransactionLineItems={onFetchTransactionLineItems}
      onSendInquiry={onSendInquiry}
      onInitializeCardPaymentData={onInitializeCardPaymentData}
      onFetchTimeSlots={onFetchTimeSlots}
    />
  );
};

export default ListingPage;
